import { CheckCircle2, FileText, Info, Sparkles } from "lucide-react";

/**
 * DocumentChecklistCard — the "what should I gather?" shortlist shown
 * on the Landing page, Registration page, and Upload page.
 *
 * Reassurance is the point: users often defer registration because they
 * don't know what to upload, or wait until they've assembled every
 * document. This card tells them:
 *   1. Here's the shortlist
 *   2. You DO NOT need to have all of them to start
 *   3. Our AI reads each doc, keeps a live checklist, and tells you what's missing
 *
 * Variants:
 *   • `compact` (Landing page hero — under CTA)
 *   • `standard` (Registration page — full card next to the form)
 *   • `inline` (Upload page — below the dropzone)
 *
 * Grafted from GreenCardsUSA.ai Module 09; CORE_DOCS rewritten for DS-260
 * consular processing per the module's INSTALL.md sister-app template.
 */

const CORE_DOCS = [
  {
    icon: FileText,
    person: "Primary applicant",
    items: [
      "Passport (biographic page)",
      "Birth certificate (long-form)",
      "Police certificate from every country lived in 12+ months since age 16",
      "Military records (if applicable)",
      "Court/prison records (if applicable)",
      "Marriage certificate (current + any prior divorces)",
    ],
  },
  {
    icon: FileText,
    person: "Each derivative applicant (spouse + children over 14)",
    items: [
      "Their own passport",
      "Their own birth certificate",
      "Police certificate (if 16+)",
    ],
  },
  {
    icon: FileText,
    person: "Sealed medical & financial",
    items: [
      "Sealed medical exam from a State Dept-designated panel physician",
      "I-864 Affidavit of Support from the U.S. petitioner (financial docs)",
      "Recent tax returns of the U.S. petitioner",
    ],
  },
];

// -------- Variant 1: Compact (Landing page hero) --------
export function DocumentChecklistCompact({ testid = "doc-checklist-compact" }) {
  return (
    <div
      className="rounded-lg p-4 text-left"
      style={{
        background: "#FFFFFF",
        border: "1px solid var(--border-stone)",
      }}
      data-testid={testid}
    >
      <div className="flex items-start gap-2.5 mb-2">
        <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--action)" }} />
        <div className="text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>
          Upload what you have — the system tracks what's missing
        </div>
      </div>
      <p className="text-[12.5px] leading-relaxed mb-2" style={{ color: "var(--text-secondary)" }}>
        Passports, birth certificates, police certificates, marriage certificate, and your NVC notice.
        You don't need every document to get started — upload the ones you have. Our AI reads each one,
        extracts the data, and keeps a live checklist showing exactly what's still needed.
      </p>
      <div
        className="text-[11.5px] leading-relaxed p-2 rounded flex items-start gap-1.5"
        style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
        data-testid={`${testid}-reassurance`}
      >
        <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>
          <strong>Don't worry.</strong> We'll give you feedback on each document you upload,
          suggestions for providing any missing ones, and even a legibility rating of every page.
        </span>
      </div>
    </div>
  );
}

// -------- Variant 2: Standard (Registration page) --------
export function DocumentChecklistStandard({ testid = "doc-checklist-standard" }) {
  return (
    <div
      className="card-surface p-5 sm:p-6"
      style={{ background: "#FFFFFF" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center"
          style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
        >
          <FileText className="w-4 h-4" />
        </div>
        <div>
          <div className="font-display font-bold text-[15px]" style={{ color: "var(--text-primary)" }}>
            Documents you'll upload after signing up
          </div>
          <div className="text-[11.5px]" style={{ color: "var(--text-secondary)" }}>
            You don't need all of these to start — upload what you have today
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {CORE_DOCS.map((group) => (
          <div key={group.person} data-testid={`doc-group-${group.person.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <div className="text-[12px] font-bold mb-1" style={{ color: "var(--text-primary)" }}>
              {group.person}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-1.5 text-[12.5px] leading-snug"
                  style={{ color: "var(--text-secondary)" }}
                >
                  <CheckCircle2 className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: "#16A34A" }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div
        className="mt-4 p-3 rounded-md text-[11.5px] leading-relaxed flex items-start gap-2"
        style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
      >
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Start with what you have.</strong> Our AI reads every document as you upload it,
          extracts the data, and maintains an auto-updated checklist showing what you've submitted
          and what's still needed. You can add missing documents later — no deadline, no penalty.
        </div>
      </div>

      <div
        className="mt-2 p-3 rounded-md text-[11.5px] leading-relaxed flex items-start gap-2"
        style={{ background: "#EFF9F1", color: "#0F5D2E", border: "1px solid #BEE5C6" }}
        data-testid={`${testid}-reassurance`}
      >
        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Don't worry.</strong> For each document you upload we'll give you clear feedback,
          suggestions for providing any missing documents, and a legibility rating of every page —
          so you'll always know exactly where your case stands.
        </div>
      </div>
    </div>
  );
}

// -------- Variant 3: Inline (Upload page — below dropzone) --------
export function DocumentChecklistInline({ testid = "doc-checklist-inline" }) {
  return (
    <div
      className="mt-4 rounded-md p-4"
      style={{ background: "#FBF6EC", border: "1px solid var(--border-stone)" }}
      data-testid={testid}
    >
      <div className="flex items-start gap-2 mb-3">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--action)" }} />
        <div className="flex-1">
          <div className="text-[13px] font-bold" style={{ color: "var(--text-primary)" }}>
            What to upload (start with what you have — add more anytime)
          </div>
          <div className="text-[11.5px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
            We read every document as it arrives and keep a live checklist of what's still needed.
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {CORE_DOCS.map((group) => (
          <div
            key={group.person}
            className="p-2.5 rounded"
            style={{ background: "#FFFFFF", border: "1px solid var(--border-stone)" }}
            data-testid={`doc-inline-${group.person.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          >
            <div className="text-[11.5px] font-bold mb-1" style={{ color: "var(--text-primary)" }}>
              {group.person}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item} className="text-[11.5px] leading-snug flex gap-1.5" style={{ color: "var(--text-secondary)" }}>
                  <CheckCircle2 className="w-2.5 h-2.5 mt-1 flex-shrink-0" style={{ color: "#16A34A" }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-3 text-[11px] italic text-center" style={{ color: "var(--text-secondary)" }}>
        Missing one or more? Upload the ones you have now — the checklist auto-updates as you add more.
      </div>

      <div
        className="mt-3 p-2.5 rounded text-[11.5px] leading-relaxed flex items-start gap-2"
        style={{ background: "#EFF9F1", color: "#0F5D2E", border: "1px solid #BEE5C6" }}
        data-testid={`${testid}-reassurance`}
      >
        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Don't worry.</strong> After each upload you'll see feedback on the document,
          suggestions for any missing items, and a legibility rating for every page — so nothing
          quietly slips through.
        </div>
      </div>
    </div>
  );
}

export default DocumentChecklistStandard;
